import { Fragment, ReactNode } from 'react';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckIcon from '@material-ui/icons/Check';
import { Button, Tooltip, TooltipTrigger } from '@backstage/ui';
import { Focusable } from 'react-aria-components';
import { ContentCard } from '../ContentCard/ContentCard';
import styles from './StepTimeline.module.css';

interface StepTimelineProps {
  steps: string[];
  activeStep?: number;
  children?: ReactNode;
  onCancel?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  isNextDisabled?: boolean;
  /** Shows a spinner on the submit button and disables the others. */
  isSubmitting?: boolean;
  /** Label of the Next button on the last step. */
  submitLabel?: string;
  /** Tooltip on the Next button while it is disabled. */
  nextDisabledTooltip?: string;
}

export const StepTimeline = ({
  steps,
  activeStep = 1,
  children,
  onCancel,
  onPrevious,
  onNext,
  isNextDisabled,
  isSubmitting,
  submitLabel = 'Create tooling group',
  nextDisabledTooltip,
}: StepTimelineProps) => {
  const isFirstStep = activeStep <= 1;
  const isLastStep = activeStep >= steps.length;
  const isPreviousDisabled = isFirstStep || isSubmitting;
  const nextButton = (
    <Button
      className={styles.nextButton}
      iconEnd={isLastStep ? undefined : <ArrowForwardIcon />}
      isDisabled={isNextDisabled}
      isPending={isSubmitting}
      onClick={onNext}
    >
      {isLastStep ? submitLabel : 'Next'}
    </Button>
  );

  return (
    <ContentCard>
      <div className={styles.row}>
        {steps.map((label, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === activeStep;
          const isCompleted = stepNumber < activeStep;
          return (
            <Fragment key={label}>
              <div className={styles.step}>
                <div
                  className={`${styles.circle} ${
                    isActive || isCompleted ? styles.circleActive : ''
                  }`}
                >
                  {isCompleted ? (
                    <CheckIcon className={styles.checkIcon} />
                  ) : (
                    stepNumber
                  )}
                </div>
                <span
                  className={`${styles.label} ${
                    isActive ? styles.labelActive : ''
                  }`}
                >
                  {label}
                </span>
              </div>
              {index < steps.length - 1 && <div className={styles.connector} />}
            </Fragment>
          );
        })}
      </div>
      {children && (
        <>
          <hr className={styles.divider} />
          {children}
        </>
      )}
      <hr className={styles.divider} />
      <div className={styles.footer}>
        <Button
          className={styles.cancelButton}
          isDisabled={isSubmitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <div className={styles.navButtons}>
          <Button
            className={`${styles.previousButton} ${
              isPreviousDisabled ? styles.previousButtonDisabled : ''
            }`}
            iconStart={<ArrowBackIcon />}
            isDisabled={isPreviousDisabled}
            onClick={onPrevious}
          >
            Previous
          </Button>
          {nextDisabledTooltip ? (
            <TooltipTrigger delay={300} isDisabled={!isNextDisabled}>
              {/* A disabled button gets no hover events, so the span triggers the tooltip. */}
              <Focusable>
                <span>{nextButton}</span>
              </Focusable>
              <Tooltip>{nextDisabledTooltip}</Tooltip>
            </TooltipTrigger>
          ) : (
            nextButton
          )}
        </div>
      </div>
    </ContentCard>
  );
};
